import { Injectable } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { take, map } from 'rxjs/operators';
import { RedirectService } from '../services/redirect.service';

@Injectable({ providedIn: 'root' })
export class RedirectResolver implements Resolve<any> {
  private lastRedirectUrl: string | null = null; // Track last redirect URL

  constructor(private redirectService: RedirectService, private router: Router) {}

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    const currentUrl = route.url.map(segment => segment.path).join('/') || '/';
    const normalizedUrl = this.normalizeUrl(currentUrl);


    // Skip API call if URL matches last redirect URL
    if (this.lastRedirectUrl && this.lastRedirectUrl === normalizedUrl) {
      return of(null);
    }

    if (this.isValidAppRoute(normalizedUrl)) {
      return of(null);
    }

    return this.redirectService.getRedirectUrl(normalizedUrl).pipe(
      take(1),
      map(response => {
        if (response && response.url && this.isValidRelativeUrl(response.url)) {
          this.lastRedirectUrl = response.url; // Store redirect URL
          return response.url;
        }
        this.lastRedirectUrl = null; // Clear if no valid redirect
        return null;
      })
    );
  }

  private isValidRelativeUrl(url: string): boolean {
    return url.startsWith('/');
  }

  private isValidAppRoute(url: string): boolean {
    const urlWithoutParams = this.normalizeUrl(url).split('?')[0];
    const routes = this.router.config;

    return routes.some(route => {
      if (route.path === urlWithoutParams) return true;
      if (route.children) {
        return route.children.some(child => {
          const fullChildPath = route.path ? `${route.path}/${child.path}` : child.path;
          return fullChildPath === urlWithoutParams;
        });
      }
      if (route.path && route.path.includes(':')) {
        const routeSegments = route.path.split('/');
        const urlSegments = urlWithoutParams.split('/');
        if (routeSegments.length === urlSegments.length) {
          return routeSegments.every((segment, i) => segment.startsWith(':') || segment === urlSegments[i]);
        }
      }
      return false;
    });
  }

  private normalizeUrl(url: string): string {
    let normalized = url.startsWith('/') ? url.slice(1) : url;
    normalized = normalized.endsWith('-') ? normalized.slice(0, -1) : normalized;
    return normalized;
  }
}